"use client"

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Testbook from "../../assets/login/testbookimg.png";
import LoginLogo from "../../assets/login/loginlogo.svg";
import { IoClose } from "react-icons/io5";
import { axiosRequest } from "../../_config/apiClient";
import { showError, showSuccess } from "../../_config/toastUtil";

export default function StudentLogin({ isOpen, onClose, identifier, loginMethod }) {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [captcha, setCaptcha] = useState("");
  const [captchaInput, setCaptchaInput] = useState("");

  // New State for Inline Errors
  const [errors, setErrors] = useState({});

  const generateCaptcha = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let text = "";
    for (let i = 0; i < 6; i++) {
      text += chars[Math.floor(Math.random() * chars.length)];
    }
    return text;
  };

  useEffect(() => {
    setCaptcha(generateCaptcha());
  }, []);

  useEffect(() => {
    if (loginMethod === 'email') {
      setEmail(identifier);
    } else {
      setPhone(identifier);
    }
  }, [loginMethod, identifier]);

  const validate = () => {
    let newErrors = {};
    if (!username.trim()) newErrors.username = "Username is required";

    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(email)) {
      newErrors.email = "Invalid email format";
    }

    if (!phone || phone.length !== 10) {
      newErrors.phone = "Phone number must be 10 digits";
    }

    if (password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (captchaInput !== captcha) {
      newErrors.captcha = "Captcha incorrect ❌";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = async () => {
    if (!validate()) return;

    try {
      const payload = {
        name: username.trim(),
        password,
        phone: phone, // Sending current phone state
        email: email.trim(), // Sending current email state
      };

      const { data } = await axiosRequest(
        'post',
        "/user/userRegisterProfileSet",
        payload,
        {
          Authorization: `Bearer ${localStorage.getItem("user")}`,
        }
      );

      showSuccess("Registration successfully 🎉");
      localStorage.setItem("profile", JSON.stringify(data));
      onClose();
      setTimeout(() => {
        navigate("/dashboard");
      }, 1000)

    } catch (err) {
      const msg = err.response?.data?.message || "Something went wrong";
      showError(typeof msg === "string" ? msg : JSON.stringify(msg));
    }
  };

  if (!isOpen) return null;

  // Helper component for error text to keep code clean
  const ErrorMsg = ({ msg }) => msg ? <p className="text-red-500 text-sm mt-1">{msg}</p> : null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[999] px-4">
      <div className="w-full max-w-[1200px] py-6 px-4 sm:px-8 md:px-20 mx-auto rounded-[10px] bg-white relative">
        <button onClick={onClose} className="absolute top-4 w-[24px] h-[24px] cursor-pointer flex items-center right-4 justify-center hover:text-gray-600 z-10 bg-white rounded-full p-1 border border-[#333333]">
          <IoClose className="text-[#333333] text-[20px]" />
        </button>

        <div className="bg-white h-[600px] w-full mx-auto rounded-2xl max-w-[1100px] grid grid-cols-1 lg:grid-cols-2 items-center">
          <div className="flex-1 md:pr-18 lg:pr-30 pl-0 ">
            <img src={LoginLogo} alt="BaapBola Logo" />
            <div>
              <h2 className="text-[23px] md:text-[28px] font-[600] tracking-tight text-[#333] mb-2">Student Exam Portal BaapBola</h2>
              <p className="text-[16px] text-[#33333399] font-[500] tracking-tight leading-[20px]">Create your account to get started</p>
            </div>

            <div className="space-y-4 p-0 mt-3 h-[300px] overflow-y-auto md:pl-2">
              <div>
                <label className="block text-[18px] font-[500] mb-2">User Name</label>
                <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className={`w-full border-[2px] rounded px-4 py-3 outline-none ${errors.username ? 'border-red-500' : 'border-[#33333399]'}`} />
                <ErrorMsg msg={errors.username} />
              </div>

              <div>
                <label className="block text-[18px] font-[500] mb-2">Email Address</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={`w-full border-[2px] rounded px-4 py-3 outline-none ${errors.email ? 'border-red-500' : 'border-[#33333399]'}`} />
                <ErrorMsg msg={errors.email} />
              </div>

              <div>
                <label className="block text-[18px] font-[500] mb-2">Phone Number</label>
                <input type="text" value={phone} maxLength={10} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))} className={`w-full border-[2px] rounded px-4 py-3 outline-none ${errors.phone ? 'border-red-500' : 'border-[#33333399]'}`} />
                <ErrorMsg msg={errors.phone} />
              </div>

              <div>
                <label className="block text-[18px] font-[500] mb-2">Password</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={`w-full border-[2px] rounded px-4 py-3 outline-none ${errors.password ? 'border-red-500' : 'border-[#33333399]'}`} />
                <ErrorMsg msg={errors.password} />
              </div>

              <div>
                <label className="block text-[18px] font-[500] mb-2">Confirm Password</label>
                <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={`w-full border-[2px] rounded px-4 py-3 outline-none ${errors.confirmPassword ? 'border-red-500' : 'border-[#33333399]'}`} />
                <ErrorMsg msg={errors.confirmPassword} />
              </div>

              <div className="flex items-center gap-5">
                <div className="flex-1 bg-[#E5E5E5] text-[#4096F6] font-semibold tracking-[6px] text-center py-3 rounded cursor-pointer">{captcha}</div>
                <button type="button" onClick={() => { setCaptcha(generateCaptcha()); setCaptchaInput(""); }} className="w-[48px] h-[48px] bg-[#4096F6] text-white rounded flex items-center justify-center text-xl">↻</button>
              </div>

              <div>
                <label className="block text-[18px] font-[500] mb-2">Enter Captcha</label>
                <input type="text" value={captchaInput} onChange={(e) => setCaptchaInput(e.target.value)} className={`w-full border-[2px] rounded px-4 py-3 outline-none ${errors.captcha ? 'border-red-500' : 'border-[#33333399]'}`} />
                <ErrorMsg msg={errors.captcha} />
              </div>
            </div>

            <button onClick={handleContinue} className="w-full mt-4 bg-[#4096F6] hover:bg-blue-600 text-white font-semibold py-3 rounded-[8px]">Continue</button>
            <p className="mt-8 text-[18px] font-[500] text-[#333333]">
              Already have an account? <button className="text-[#4096F6] hover:underline">Login</button>
            </p>
          </div>

          <div className="hidden lg:flex flex-1 items-center justify-center">
            <img src={Testbook} alt="Banner" className="w-full h-full object-cover" />
          </div>
        </div>
      </div>
    </div>
  );
}